import { getRandomUUID } from 'shared/lib/core/lib/utils';
import {
	selectHallmapSelectedTables,
	selectisConflict,
} from 'entities/hallmap';
import { useAppDispatch, useAppSelector } from 'shared/lib/store';
import {
	getWaitingOrders,
	resetBookingData,
	resetOrderDetail,
	selectBookingApplicationMod,
	selectOrderDetail,
	setBookingApplicationMod,
	setBookingThreadMod,
	updateBookingId,
	useOrderState,
} from 'entities/orders';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useBookingSlots } from 'features/booking/slots';

import { getDateRangeFromToday } from 'shared/lib/datepicker';

import { useModal } from 'shared/lib/modal';

export const useBookingState = () => {
	const isConflict = useAppSelector(selectisConflict);
	const { currentActive } = useBookingSlots();
	const selectedResources = useAppSelector(selectHallmapSelectedTables);
	const [isDisabledBooking, setDisableBooking] = useState(false);
	const idempotencyKey = useRef(crypto?.randomUUID ? crypto.randomUUID() : getRandomUUID());
	const [isNewBooking, setIsNewBooking] = useState(true);
	const dispatch = useAppDispatch();
	const order = useAppSelector(selectOrderDetail);
	const { closeModal } = useModal();
	const [bookingError, setBookingError] = useState<string | null>(null);
	const { isWaitingList, isVisitProgress } = useOrderState(order);
	const appMod = useAppSelector(selectBookingApplicationMod);

	const getWaitingOrdersList = useCallback(() => {
		const [start, end] = getDateRangeFromToday(true);
		dispatch(
			getWaitingOrders({
				dt_start__gte: start.toISOString(),
				dt_end__lte: end.toISOString(),
			}),
		);
	}, [dispatch]);

	const successClose = useCallback(
		(force = false) => {
			closeModal();

			if (appMod) {
				dispatch(setBookingApplicationMod({ isWork: false }));
			}

			if (!isWaitingList || force) {
				dispatch(resetOrderDetail());
				dispatch(resetBookingData());
				dispatch(setBookingThreadMod({ isWork: false }));
			}
		},
		[closeModal, dispatch, isWaitingList, appMod],
	);

	const disableBooking = () => {
		setDisableBooking(true);
	};

	const enableBooking = () => {
		setDisableBooking(false);
	};

	const clearError = () => {
		setBookingError(null);
	};

	useEffect(() => {
		if (isConflict || (isVisitProgress ? false : !currentActive) || !selectedResources.length) {
			setDisableBooking(true);
			idempotencyKey.current = crypto?.randomUUID ? crypto.randomUUID() : getRandomUUID();
		} else {
			setDisableBooking(false);
		}
		// eslint-disable-next-line
	}, [isConflict, currentActive, selectedResources]);

	useEffect(() => {
		if (order) {
			setIsNewBooking(false);
			dispatch(updateBookingId({ id: order.id }));
		}
	}, [order, dispatch]);

	return {
		getWaitingOrdersList,
		successClose,
		setDisableBooking,
		disableBooking,
		enableBooking,
		isDisabledBooking,
		isNewBooking,
		idempotencyKey,
		bookingError,
		setBookingError,
		clearError,
	};
};
